import { useEffect } from 'react';
import type { ReactNode } from 'react';

/**
 * Branding colors as carried by TakiConfig.branding.colors (@taki/runtime-config).
 *
 * In the Taki brand book v2 the client branding drives mainly the ACCENT; the
 * rest of the palette comes from the fixed design tokens (tokens.css). Each
 * value is a solid CSS color string: hex ("#cbfb45"), rgb(), or hsl().
 *
 * `primary` is required (it becomes --accent). `background` / `foreground` are
 * optional overrides for --bg / --ink-900; when omitted the token defaults stand.
 */
export interface ThemeColors {
  /** Drives the accent (--accent). Solid hex/rgb/hsl string. */
  primary: string;
  /** Optional override for the app background (--bg). */
  background?: string;
  /** Optional override for the default text color (--ink-900). */
  foreground?: string;
}

export interface ThemeProviderProps {
  /** Branding colors loaded at runtime from the client config. */
  colors: ThemeColors;
  children: ReactNode;
}

/**
 * Normalize a CSS color string into a solid value writable on a CSS variable.
 *
 * Tokens are solid hex (no HSL channels), so most inputs pass through unchanged.
 * We only normalize shorthand 3-digit hex to 6-digit for consistency; rgb()/hsl()
 * and full hex are handed back as-is. Unknown/empty input returns null so the
 * caller can skip the assignment and keep the token default.
 */
function toSolidColor(input: string | undefined): string | null {
  if (input === undefined) return null;
  const value = input.trim();
  if (value === '') return null;

  // Expand shorthand hex (#abc -> #aabbcc).
  const shortHex = value.match(/^#([0-9a-fA-F]{3})$/);
  if (shortHex?.[1] !== undefined) {
    const [r, g, b] = shortHex[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  // Full hex, rgb()/rgba(), hsl()/hsla(): valid solid CSS colors, pass through.
  if (
    /^#[0-9a-fA-F]{6}$/.test(value) ||
    /^rgba?\(/i.test(value) ||
    /^hsla?\(/i.test(value)
  ) {
    return value;
  }

  // Unknown format: hand it back untouched (keeps callers passing named colors etc. working).
  return value;
}

/**
 * Applies the client's branding colors (from @taki/runtime-config) onto :root
 * as the design-token CSS variables consumed by @taki/ui and the Tailwind
 * preset. Render once near the root of the app, inside the config boundary.
 *
 * v2: branding pilots the accent. `primary` -> --accent; optional `background`
 * -> --bg; optional `foreground` -> --ink-900. Everything else stays on the
 * fixed token defaults from tokens.css.
 *
 * Wraps children unchanged; it only manages document-level CSS variables.
 */
export function ThemeProvider({ colors, children }: ThemeProviderProps) {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    const assignments: Record<string, string> = {};
    const accent = toSolidColor(colors.primary);
    if (accent !== null) assignments['--accent'] = accent;

    const background = toSolidColor(colors.background);
    if (background !== null) assignments['--bg'] = background;

    const foreground = toSolidColor(colors.foreground);
    if (foreground !== null) assignments['--ink-900'] = foreground;

    const previous: Array<[string, string]> = [];
    for (const [name, color] of Object.entries(assignments)) {
      previous.push([name, root.style.getPropertyValue(name)]);
      root.style.setProperty(name, color);
    }

    return () => {
      for (const [name, value] of previous) {
        if (value === '') {
          root.style.removeProperty(name);
        } else {
          root.style.setProperty(name, value);
        }
      }
    };
  }, [colors.primary, colors.background, colors.foreground]);

  return children;
}
